/* eslint-disable @typescript-eslint/no-empty-function */
// services
import AbstractSymbolsSearchService from 'modules/TradingChart/components/TradingView/utils/services/symbolsSearch/AbstractSymbolsSearchService';
import AppService from 'modules/App';
// typings
import { IBarsProviderRest } from 'modules/TradingChart/components/TradingView/utils/services/barsProviders/BarsProviderRest/consts';
import LiveDataService from 'modules/LiveData';
import FormatService from 'modules/Format';
import { MARKET_FEEDER_MODES } from 'modules/LiveData/constants';
import { Exchange } from 'modules/Search/constants';
import {
  IDatafeedQuotesApi,
  IDatafeedChartApi,
  IBasicDataFeed,
  ResolveCallback,
  ErrorCallback,
  HistoryCallback,
  SearchSymbolsCallback,
  SubscribeBarsCallback,
  LibrarySymbolInfo,
  OnReadyCallback,
  QuotesCallback,
  ResolutionString,
  ServerTimeCallback,
  PeriodParams,
  DOMCallback,
  QuoteData,
} from '../../../sources/charting_library.min';
import { BarsData } from '../../services/barsProviders/typings';
import AbstractBarsProviderService from '../../services/barsProviders';
// utils
import { removeReloadSuffix } from '../../services/symbolsSearch/utils';

export class DataProvider
  implements IBasicDataFeed, IDatafeedQuotesApi, IDatafeedChartApi {
  private subscriptionListenerGuid = 'main';
  symbolSelectCallback?: ChartSymbolSelectedCallback;

  symbolsSearchService: AbstractSymbolsSearchService;
  barsProviderService: AbstractBarsProviderService;
  barsProviderRest: IBarsProviderRest;
  onDataCallbackTimeout: ReturnType<typeof setTimeout> | null = null;
  onReadyTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(params: {
    symbolsSearchService: AbstractSymbolsSearchService;
    barsProviderService: AbstractBarsProviderService;
    symbolSelectCallback?: ChartSymbolSelectedCallback;
    isSymbolsSearchAvailable?: boolean;
    barsProviderRest: IBarsProviderRest;
  }) {
    // Instances of services
    this.symbolsSearchService = params.symbolsSearchService;
    this.barsProviderService = params.barsProviderService;
    this.barsProviderRest = params.barsProviderRest;

    this.symbolSelectCallback = params.symbolSelectCallback;
  }

  public onReady(callback: OnReadyCallback): void {
    // NOTE: Function callback is heavy, so it needs to be done async
    if (this.onReadyTimeout) {
      clearTimeout(this.onReadyTimeout);
    }
    this.onReadyTimeout = setTimeout(
      () => callback(this.getConfiguration()),
      0,
    );
  }

  private getConfiguration() {
    return {
      supports_group_request: false,
      supports_marks: false,
      supports_time: true,
      supports_timescale_marks: false,
      symbols_types: [
        { name: 'ALL', value: 'ALL' },
        { name: 'Cash', value: 'CASH' },
        { name: 'F&O', value: 'FUTOPT' },
        { name: 'Currency', value: 'CURRENCY' },
        { name: 'Commodity', value: 'COMMODITY' },
      ],
    };
  }

  getQuotesData = (symbols: string[]) => symbols
    .map(item => ({
      s: 'ok',
      n: item,
      v: {},
    }))

  public getQuotes(symbols: string[], onDataCallback: QuotesCallback) {
    // Initially add quotes data with empty values then,
    // update quotes data from subscribeQuotes using onRealtimeCallback method
    if (this.onDataCallbackTimeout) {
      clearTimeout(this.onDataCallbackTimeout);
    }
    this.onDataCallbackTimeout = setTimeout(() =>
      onDataCallback(this.getQuotesData(symbols) as QuoteData[]),
      0);
  }

  public async subscribeQuotes(
    symbols: string[],
    fastSymbols: string[],
    onRealtimeCallback: QuotesCallback,
    listenerGuid: string,
  ) {
    const items = symbols.map(symbol =>
      FormatService.decodeCustomTvExchangeToken(symbol),
    );

    if (items.length) {
      const uniqueIds = await LiveDataService.getSymbolInstrumentKeys(items);
      // Subscribe to OPTION_CHAIN mode since volume data is not available in LTPC mode
      LiveDataService.subscribeScrip(
        uniqueIds, MARKET_FEEDER_MODES.OPTION_CHAIN,
      );
    }

    if (!LiveDataService.quotesData[listenerGuid]) {
      LiveDataService.quotesData[listenerGuid] = onRealtimeCallback;
    }

    LiveDataService.setTokensWithGuids(listenerGuid, items);
  }

  public async unsubscribeQuotes(listenerGuid: string) {
    const symbols = LiveDataService.tokensWithGuids.get(listenerGuid) || [];

    const uniqueIds = await LiveDataService.getSymbolInstrumentKeys(symbols);

    LiveDataService.unsubscribeScrip(
      uniqueIds, MARKET_FEEDER_MODES.OPTION_CHAIN,
    );
    LiveDataService.removeTokensWithProvidedGuid(listenerGuid);
    delete LiveDataService.quotesData[listenerGuid];
  }

  public getMarks(): void { }

  public getServerTime(callback: ServerTimeCallback): void {
    callback(Date.now() / 1000);
  }

  public searchSymbols(
    userInput: string,
    exchange: Exchange,
    symbolType: SearchTabKeys,
    onResult: SearchSymbolsCallback,
  ): void {
    this.symbolsSearchService.searchSymbolsByUserInput(
      userInput,
      exchange,
      symbolType,
      onResult,
    );
  }

  public resolveSymbol(
    symbolTicker: string,
    onResolve: ResolveCallback,
    onError: ErrorCallback,
  ) {
    const currentSymbol = FormatService.decodeCustomTvExchangeToken(
      symbolTicker,
    );

    this.symbolsSearchService.resolveSymbolByToken(
      currentSymbol,
      (response: LibrarySymbolInfo) => {
        const customOIIndicator = symbolTicker.includes('#customOI');
        const updatedResponse = {
          ...response,
          ...(customOIIndicator && {
            ticker: removeReloadSuffix(symbolTicker),
            full_name: removeReloadSuffix(symbolTicker),
          }),
        };
        onResolve(updatedResponse);
      },
      onError,
    );
  }

  public getBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    periodParams: PeriodParams,
    onResult: HistoryCallback,
    onError: ErrorCallback,
  ): void {
    if (AppService.isOffline) {
      setTimeout(() => {
        onError('Currently Offline');
      }, 0);
      return;
    }
    this.barsProviderRest
      .getHistoricalBars(symbolInfo, resolution, periodParams)
      .then((result: BarsData) => {
        onResult(result.bars, result.meta);
      })
      .catch((error: string) => {
        onError(error);
      });
  }

  public subscribeBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    onTick: SubscribeBarsCallback,
    ticker: string,
    onResetCacheNeededCallback: () => void,
  ): void {
    this.barsProviderService.subscribeToLiveBars(
      symbolInfo,
      resolution,
      onTick,
      ticker,
      this.subscriptionListenerGuid,
      onResetCacheNeededCallback,
    );
  }

  public unsubscribeBars(ticker: string): void {
    this.barsProviderService.unsubscribeFromLiveBars(
      ticker,
      this.subscriptionListenerGuid,
    );
  }

  public subscribeDepth(symbol: string, callback: DOMCallback): string {
    const subscriberUID = FormatService.generateUUID();
    this.subscribeToDepthData(symbol, callback, subscriberUID);
    return subscriberUID;
  }

  public async subscribeToDepthData(
    symbol: string,
    callback: DOMCallback,
    subscriberUID: string,
  ) {
    const exchangeToken = FormatService.decodeCustomTvExchangeToken(symbol);

    const uniqueIds = await LiveDataService.getSymbolInstrumentKeys([
      exchangeToken,
    ]);

    LiveDataService.subscribeScrip(uniqueIds, MARKET_FEEDER_MODES.FULL_D30);
    LiveDataService.setDepthTokenWithSubscriberUID(subscriberUID, uniqueIds[0]);
    LiveDataService.setDomDetails(exchangeToken, callback);
  }

  public unsubscribeDepth(subscriberUID: string) {
    const uniqueId =
      LiveDataService.depthTokenWithSubscriberUID.get(subscriberUID) ?? '';

    LiveDataService.unsubscribeScrip([uniqueId], MARKET_FEEDER_MODES.FULL_D30);
    LiveDataService.removeDepthTokenWithSubscriberUID(subscriberUID);
    LiveDataService.resetDomDetails();
  }
}
